import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, Redirect, useHistory } from "react-router-dom";
import {
  FaBookOpen,
  FaCalendarAlt,
  FaCheckCircle,
  FaClipboardList,
  FaHome,
  FaSyncAlt,
  FaTimesCircle,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import BackButton from "../ChildComponent/BackButton";
import SelectClass from "../ChildComponent/SelectClass";
import CalendarView from "../ChildComponent/CalendarView";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

//Created By Cursor
function formatIso(date) {
  const d = date.getDate();
  const m = date.getMonth() + 1;
  const y = date.getFullYear();
  const mm = m > 9 ? String(m) : `0${m}`;
  const dd = d > 9 ? String(d) : `0${d}`;
  return {
    iso: `${y}-${mm}-${dd}`,
    display: `${dd}-${mm}-${y}`,
    month: mm,
    year: y,
  };
}

//Created By Cursor
function dayCalendarStatus(dayRow) {
  const dayType = String(dayRow?.day || "").trim();
  if (/^sunday$/i.test(dayType)) return "Sunday";
  if (/^holiday$/i.test(dayType)) return "Holiday";
  const subjects = Array.isArray(dayRow?.subjects) ? dayRow.subjects : [];
  const anyAssigned = subjects.some((s) => /^assigned$/i.test(s?.status));
  return anyAssigned ? "Assigned" : "Not Assigned";
}

//Created By Cursor
function KpiChip({ tone, icon: Icon, label, value }) {
  return (
    <div className={`sm-kpi-chip sm-kpi-chip--${tone}`}>
      <span className="sm-kpi-chip__icon" aria-hidden>
        {Icon ? <Icon /> : null}
      </span>
      <div className="sm-kpi-chip__text">
        <span className="sm-kpi-chip__label">{label}</span>
        <span className="sm-kpi-chip__value">{value}</span>
      </div>
    </div>
  );
}

//Created By Cursor
export default function HomeWork() {
  const history = useHistory();
  const loggedIn = localStorage.getItem("token") != null;

  const [currentDate, setCurrentDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(false);

  const [className, setClassName] = useState("");
  const [sectionName, setSectionName] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [selectedClassPosition, setSelectedClassPosition] = useState(-1);

  const [monthRows, setMonthRows] = useState([]);
  const [events, setEvents] = useState([]);

  const monthParts = useMemo(() => formatIso(currentDate), [currentDate]);
  const selectedParts = useMemo(
    () => formatIso(selectedDate),
    [selectedDate]
  );
  const classLabel = sectionName
    ? `${className}:${sectionName}`
    : className || "Select a class";
  const monthLabel = useMemo(
    () =>
      currentDate.toLocaleString("default", {
        month: "long",
        year: "numeric",
      }),
    [currentDate]
  );

  const selectedDay = useMemo(() => {
    return (
      monthRows.find((row) => row.homework_date === selectedParts.iso) || null
    );
  }, [monthRows, selectedParts.iso]);

  const subjects = useMemo(
    () => (Array.isArray(selectedDay?.subjects) ? selectedDay.subjects : []),
    [selectedDay]
  );

  const dayType = String(selectedDay?.day || "").trim();
  const isOffDay =
    /^sunday$/i.test(dayType) || /^holiday$/i.test(dayType);

  const liveCounts = useMemo(() => {
    const assigned = subjects.filter((s) =>
      /^assigned$/i.test(s?.status)
    ).length;
    return {
      total: subjects.length,
      assigned,
      pending: Math.max(0, subjects.length - assigned),
    };
  }, [subjects]);

  const callBackClassSelect = useCallback(
    (
      class_id,
      section_id,
      session_id,
      selected_position,
      class_name,
      section_name,
      _session_name,
      START_DATE,
      END_DATE
    ) => {
      setClassId(class_id);
      setClassName(class_name || "");
      setSectionName(section_name || "");
      setSectionId(section_id);
      setSessionId(session_id);
      setStartDate(START_DATE || "");
      setEndDate(END_DATE || "");
      setSelectedClassPosition(selected_position);
      setMonthRows([]);
      setEvents([]);
    },
    []
  );

  const loadMonthHomework = useCallback(async () => {
    if (!classId || !sectionId || !monthParts.month || !monthParts.year) {
      return;
    }
    setLoading(true);
    try {
      const schoolCode = SessionManager.getSchoolCode();
      const params = {
        school_code: schoolCode,
        class_id: classId,
        section_id: sectionId,
        month: monthParts.month,
        year: String(monthParts.year),
      };
      const url = `${API_BASE()}getParticularClassHomeWork.php`;
      console.log("Full URL: ", `${url}?${new URLSearchParams(params)}`);

      const response = await axios.get(url, { params }).then((res) => res.data);
      if (response?.api_status === "OK") {
        const rows = Array.isArray(response.data) ? response.data : [];
        setMonthRows(rows);
        setEvents(
          rows.map((item) => ({
            DATE: item.homework_date,
            STATUS: dayCalendarStatus(item),
          }))
        );
      } else {
        setMonthRows([]);
        setEvents([]);
        myToast.notify_warning(
          response?.api_remark || "Failed to load homework calendar."
        );
      }
    } catch (error) {
      console.error(error);
      setMonthRows([]);
      setEvents([]);
      myToast.notify_warning("Failed to load homework calendar.");
    } finally {
      setLoading(false);
    }
  }, [classId, sectionId, monthParts.month, monthParts.year]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (selectedClassPosition >= 0 && classId && sectionId) {
      loadMonthHomework();
    }
  }, [
    selectedClassPosition,
    currentDate,
    classId,
    sectionId,
    loadMonthHomework,
  ]);

  const handlePrevMonth = useCallback(() => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() - 1);
      d.setDate(1);
      return d;
    });
  }, []);

  const handleNextMonth = useCallback(() => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + 1);
      d.setDate(1);
      return d;
    });
  }, []);

  const displayCalendarEvents = useCallback((date) => {
    setSelectedDate(date);
  }, []);

  const assignPath = useCallback(
    (subject) => {
      const params = new URLSearchParams({
        class_id: classId || "",
        section_id: sectionId || "",
        session_id: sessionId || "",
        subject_id: subject?.subject_id || "",
        subject_name: subject?.subject_name || "",
        class_name: classLabel,
        date: selectedParts.iso,
        date_display: selectedParts.display,
      });
      return `/AdminMain/HomeworkAssign?${params.toString()}`;
    },
    [classId, sectionId, sessionId, classLabel, selectedParts]
  );

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  return (
    <div
      className="content payroll-page sm-page hw-page"
      style={{ fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <span style={{ color: COLORS.white }}>Homework</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell hw-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaBookOpen />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Homework</h1>
              <p className="sm-hero__sub">
                {classLabel} · {monthLabel}
                {dayType ? ` · ${dayType}` : ""}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className={`pp-icon-btn${loading ? " spinning" : ""}`}
              onClick={loadMonthHomework}
              disabled={loading || !classId}
              aria-label="Refresh"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="hw-body">
          <div className="hw-filters">
            <SelectClass
              action={callBackClassSelect}
              selected_position={selectedClassPosition}
            />
          </div>

          {!classId ? (
            <div className="hw-empty">Select a class to view homework.</div>
          ) : (
            <>
              <div className="hw-calendar-wrap">
                <CalendarView
                  events={events}
                  disableFutureDates={true}
                  selectedDate={selectedDate}
                  onDayPress={displayCalendarEvents}
                  onDayLongPress={() => {}}
                  onPrevPress={handlePrevMonth}
                  onNextPress={handleNextMonth}
                  start_date={startDate}
                  end_date={endDate}
                  subtitle="Select a date to view subject homework"
                  legendVariant="homework"
                />
              </div>

              <div className="hw-meta">
                <div className="hw-meta__item">
                  <span>Class</span>
                  <strong>{classLabel}</strong>
                </div>
                <div className="hw-meta__item">
                  <span>Selected date</span>
                  <strong>{selectedParts.display}</strong>
                </div>
                <div className="hw-meta__item">
                  <span>Day</span>
                  <strong>{dayType || "—"}</strong>
                </div>
              </div>

              {/^sunday$/i.test(dayType) ? (
                <div className="hw-banner hw-banner--sunday">Sunday</div>
              ) : null}
              {/^holiday$/i.test(dayType) ? (
                <div className="hw-banner hw-banner--holiday">Holiday</div>
              ) : null}

              {subjects.length > 0 ? (
                <div className="sm-kpi-row hw-kpis" aria-label="Homework totals">
                  <KpiChip
                    tone="sky"
                    icon={FaClipboardList}
                    label="Subjects"
                    value={liveCounts.total}
                  />
                  <KpiChip
                    tone="mint"
                    icon={FaCheckCircle}
                    label="Assigned"
                    value={liveCounts.assigned}
                  />
                  <KpiChip
                    tone="sand"
                    icon={FaTimesCircle}
                    label="Pending"
                    value={liveCounts.pending}
                  />
                  <KpiChip
                    tone="blue"
                    icon={FaCalendarAlt}
                    label="Date"
                    value={selectedParts.display}
                  />
                </div>
              ) : null}

              {loading && !subjects.length ? (
                <div className="hw-loading">
                  <MyLoader />
                </div>
              ) : null}

              <section className="hw-subjects">
                <div className="hw-subjects__head">
                  <h2>Subject status</h2>
                  <p>
                    {classLabel} · {selectedParts.display}
                  </p>
                </div>

                {!loading && !subjects.length ? (
                  <div className="hw-empty hw-empty--soft">
                    {isOffDay
                      ? "No subject homework on this day."
                      : "No subjects found for this class."}
                  </div>
                ) : (
                  <div className="hw-subject-grid">
                    {subjects.map((subject) => {
                      const assigned = /^assigned$/i.test(subject?.status);
                      const hwList = Array.isArray(subject?.class_homework)
                        ? subject.class_homework
                        : [];
                      const preview = hwList[0]?.home_work || "";
                      return (
                        <article
                          key={subject.subject_id || subject.subject_name}
                          className={`hw-subject-card${
                            assigned ? " is-assigned" : " is-pending"
                          }`}
                        >
                          <div className="hw-subject-card__top">
                            <h3>{subject.subject_name || "Subject"}</h3>
                            <span
                              className={`hw-subject-badge${
                                assigned ? " is-assigned" : ""
                              }`}
                            >
                              {assigned ? "Assigned" : "Not Assigned"}
                            </span>
                          </div>
                          {preview ? (
                            <p className="hw-subject-card__preview">
                              {preview}
                            </p>
                          ) : (
                            <p className="hw-subject-card__preview is-muted">
                              No homework text yet.
                            </p>
                          )}
                          <Link
                            to={assignPath(subject)}
                            className="hw-subject-card__cta"
                          >
                            {assigned ? "View / Update" : "Assign Homework"}
                          </Link>
                        </article>
                      );
                    })}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
